import {
  configureStore,
  Action,
  Reducer,
  AnyAction,
  createSelector,
} from '@reduxjs/toolkit';
import thunk, { ThunkAction, ThunkMiddleware } from 'redux-thunk';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import logger from 'redux-logger';
import storage from 'redux-persist/lib/storage';
import { resetAuthorizationToken } from '../api';
import { rootReducer } from './rootReducer';

// const rootReducer = combineReducers({
//   auth,
//   common,
// })

const reducerProxy: Reducer = (state: RootState, action: AnyAction) => {
  // on logout reset redux state
  if (action.type === 'auth/logout') {
    localStorage.removeItem('token');
    resetAuthorizationToken();
    return rootReducer({} as RootState, action);
  }
  return rootReducer(state, action);
};

const persistConfig = {
  debug: false,
  key: 'root',
  keyPrefix: 'v.1',
  storage,
  blacklist: [],
  // add reducer name to persist
  whitelist: ['auth', 'common', 'riskCenter', 'dashboard'],
};

// Guard against whitelist entries that don't match a real rootReducer key
// (e.g. a typo) — such entries silently persist nothing, with no error.
// There's no test runner in this repo to assert this in, so it's checked
// here at startup instead, dev-only so it can never affect production.
if (process.env.NODE_ENV !== 'production') {
  const knownReducerKeys = Object.keys(
    rootReducer(undefined, { type: '@@INIT' }),
  );
  const unknownWhitelistKeys = persistConfig.whitelist.filter(
    key => !knownReducerKeys.includes(key),
  );
  if (unknownWhitelistKeys.length) {
    throw new Error(
      `redux-persist whitelist references reducer key(s) that don't exist: ${unknownWhitelistKeys.join(
        ', ',
      )}. Check for typos against rootReducer.ts.`,
    );
  }
}

const persistedReducer = persistReducer(persistConfig, reducerProxy);

const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: getDefaultMiddleware =>
    process.env.mode === 'dev'
      ? getDefaultMiddleware({ serializableCheck: false }).concat(
          thunk as ThunkMiddleware,
          logger,
        )
      : getDefaultMiddleware({ serializableCheck: false }).concat(
          thunk as ThunkMiddleware,
        ),
});
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
const stateSelector = (state: RootState) => state;

export const currentActiveChat = createSelector(
  stateSelector,
  (state: RootState) => {
    return state.chat.activeChatId &&
      state.chat.chatMap[state.chat.activeChatId]
      ? state.chat.chatMap[state.chat.activeChatId]
      : {};
  },
);

export default store;
